import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { images } from '../assets/images';

// Service data definitions
const servicesData = {
  'farm-price-tracking': {
    id: 'farm-price-tracking',
    title: 'Farm Produce Price Tracking',
    description: 'Our real-time price tracking system helps farmers stay informed about current market prices for their produce across different regions.',
    image: images.analytics || '',
    features: [
      'Real-time market price updates',
      'Historical price data analysis',
      'Price trend predictions',
      'Regional price comparison',
      'SMS and email price alerts',
      'Direct connection to buyers'
    ],
    benefits: [
      'Make informed decisions about when to sell',
      'Negotiate better prices based on market data',
      'Plan your harvesting schedule strategically',
      'Identify the best markets for your produce',
      'Reduce dependency on middlemen'
    ],
    content: `Our Farm Produce Price Tracking system is designed to empower farmers with market intelligence that was previously only available to large agricultural corporations.

    Using a combination of data from markets, government sources, and our network of verified buyers, we provide up-to-the-minute pricing information for a wide range of agricultural products.
    
    The system allows you to track prices across different counties and markets, helping you identify where your produce will fetch the best prices. You can set alerts for price thresholds, so you'll know immediately when the market is favorable for selling.
    
    Historical data analysis helps you understand seasonal trends and make better planting decisions based on when certain crops are likely to fetch premium prices.
    
    Our predictive analytics can forecast price trends based on factors such as weather patterns, import/export data, and consumer demand, giving you a crucial edge in planning your farm operations.`
  },
  'ai-farming-practices': {
    id: 'ai-farming-practices',
    title: 'AI-assisted Best Farming Practices',
    description: 'Get personalized recommendations for your farm using our advanced AI algorithms that analyze your specific farming conditions.',
    image: images.innovation || '',
    features: [
      'Personalized crop recommendations',
      'Pest and disease detection',
      'Optimal planting schedules',
      'Soil health monitoring',
      'Weather-based farming advice',
      'Water management optimization'
    ],
    benefits: [
      'Increase crop yields through optimal practices',
      'Reduce crop losses from pests and diseases',
      'Improve soil fertility and sustainability',
      'Optimize resource usage (water, fertilizer)',
      'Adapt to changing climate conditions'
    ],
    content: `Our AI-assisted farming system combines the latest in artificial intelligence with agricultural science to provide you with farming advice tailored to your specific conditions.
    
    By analyzing data from your farm, including soil composition, local weather patterns, topography, and water availability, our AI system generates recommendations that are uniquely suited to your circumstances.
    
    The system continuously learns and improves as more data is gathered, making its recommendations increasingly accurate over time. You can upload photos of your crops for instant pest and disease detection, along with treatment recommendations.
    
    Our AI can also help you determine the optimal planting and harvesting schedules based on historical weather data and forecasts, helping you maximize yields and minimize losses.
    
    We also provide specific advice on soil improvement, water conservation, and sustainable farming practices that can improve your farm's productivity while preserving the environment for future generations.`
  },
  'produce-management': {
    id: 'produce-management',
    title: 'Produce Management and Consulting',
    description: 'Expert guidance on crop management throughout the growing cycle and strategic advice on market trends.',
    image: images.sustainable || '',
    features: [
      'Personalized crop management plans',
      'Expert agricultural consultants',
      'Post-harvest handling guidelines',
      'Storage optimization advice',
      'Market trend analysis',
      'Export opportunity identification'
    ],
    benefits: [
      'Improve crop quality and market value',
      'Reduce post-harvest losses',
      'Extend shelf life of produce',
      'Access premium markets including export',
      'Develop sustainable farming strategies'
    ],
    content: `Our Produce Management and Consulting service provides comprehensive support throughout the entire agricultural value chain, from pre-planting planning to post-harvest handling and marketing.
    
    Our team of experienced agricultural experts works closely with you to develop customized crop management plans based on your specific goals, resources, and local conditions. We provide regular check-ins and adjustments as the growing season progresses.
    
    Post-harvest, we offer guidance on handling, processing, and storage techniques that maintain produce quality and extend shelf life. Our experts can help you implement cost-effective solutions that significantly reduce losses.
    
    The consulting team also provides market intelligence and helps identify opportunities for selling your produce at premium prices, including export markets that may not be readily accessible to individual farmers.
    
    We also assist with certification processes for organic, fair trade, and other specialty designations that can increase the value of your produce in both domestic and international markets.`
  },
  'import-export-tracking': {
    id: 'import-export-tracking',
    title: 'Government Import/Export Tracking',
    description: 'Stay compliant with agricultural regulations and track import/export data to identify market opportunities.',
    image: images.tractorSunset || '',
    features: [
      'Regulatory compliance guidance',
      'Import/export documentation assistance',
      'Market access requirements',
      'Tariff and quota information',
      'Trade agreement benefits',
      'Cross-border logistics support'
    ],
    benefits: [
      'Avoid costly regulatory mistakes',
      'Reduce delays at borders',
      'Access international markets confidently',
      'Identify profitable export opportunities',
      'Understand import competition'
    ],
    content: `Our Government Import/Export Tracking service helps farmers and agricultural businesses navigate the complex world of international trade with confidence.
    
    We provide up-to-date information on regulatory requirements for different export destinations, helping you ensure that your produce meets all necessary standards and has the required documentation.
    
    Our system tracks changes in import/export regulations, tariffs, and quotas, alerting you to any developments that could affect your business. We also help you take advantage of preferential trade agreements that could reduce costs and improve market access.
    
    For exporters, we provide market intelligence on international demand and pricing, helping you identify the most profitable opportunities. For those facing import competition, we offer analysis to help you understand market dynamics and adapt your strategies accordingly.
    
    Our team can also assist with logistics planning, documentation preparation, and connections to reliable service providers for international shipping and distribution.`
  }
};

const ServiceDetails: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  
  // Find the service data based on the URL parameter
  const service = serviceId ? servicesData[serviceId as keyof typeof servicesData] : null;
  
  // If service not found, show a message
  if (!service) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4">Service not found</Typography>
        <Button 
          component={Link} 
          to="/" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }
  
  return (
    <Box sx={{ bgcolor: 'background.default', pt: 4, pb: 8 }}>
      {/* Hero Section with Image */}
      <Box 
        sx={{ 
          height: '40vh', 
          width: '100%', 
          position: 'relative',
          backgroundImage: `url(${service.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mb: 4,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <Container 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography variant="h2" color="white" gutterBottom>
            {service.title}
          </Typography>
          <Typography variant="h6" color="white">
            {service.description}
          </Typography>
        </Container>
      </Box>
      
      <Container>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            Home
          </Link>
          <Link to="/services" style={{ textDecoration: 'none', color: 'inherit' }}>
            Services
          </Link>
          <Typography color="text.primary">{service.title}</Typography>
        </Breadcrumbs>
        
        {/* Main Content Section */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              About {service.title}
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', mb: 4 }}>
              {service.content}
            </Typography>
            
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Key Benefits
            </Typography>
            
            <List>
              {service.benefits.map((benefit, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
            
            <Box sx={{ mt: 4, mb: 2 }}>
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                component={Link}
                to="/contact"
              >
                Contact Us for More Information
              </Button>
            </Box>
          </Grid>
          
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Features
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                {service.features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Related Services
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {Object.values(servicesData)
                .filter(s => s.id !== service.id)
                .slice(0, 3)
                .map((relatedService) => (
                  <Box key={relatedService.id} sx={{ mb: 2 }}>
                    <Link 
                      to={`/services/${relatedService.id}`} 
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography color="primary" variant="subtitle1">
                        {relatedService.title}
                      </Typography>
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      {relatedService.description.substring(0, 80)}...
                    </Typography>
                  </Box>
                ))
              }
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ServiceDetails; 